import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { TimeAgo } from '@pkg/classes';
import { DepButton, DepButtonDisplay } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { regexPattern } from '@pkg/utils';

import { apiFactory, type EmojiId, Transfers, TransferStatus } from '@app/client';
import { prefetch2FA } from '@app/helpers';
import { addNotification } from '@app/store/actions/notification';
import { setTransferData } from '@app/store/actions/transfers';
import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import { FieldEmail, type FieldEmailProps } from '../../../forms';
import '../YatTransferSheet.scss';

export interface InitiateTransferProps {
    eid: EmojiId;
}

function validateEmail(value = '') {
    const matches = value.match(regexPattern.validate.email);
    return Boolean(matches?.length);
}

export function InitiateTransfer({ eid }: InitiateTransferProps) {
    const [translate] = useTranslation(['transfers', 'translation', 'common']);
    const blockedUntil = useEmojisStore((state) => state.blockedUntil);

    const [receiverEmail, setReceiverEmail] = useState('');
    const [invalid, setInvalid] = useState(false);
    const [errorLabel, setErrorLabel] = useState('');
    const [transferBlocked, setTransferBlocked] = useState(false);

    const dispatch = useAppDispatch();
    const userEmail = useAppSelector((state) => state.user?.userAccount?.user?.email);

    const isOwnEmail = receiverEmail === userEmail?.trim().toLowerCase();
    const validated = validateEmail(receiverEmail) && !isOwnEmail;

    const handleEmailChange: FieldEmailProps['onChange'] = (incomingValue) => {
        const parsedValue = `${incomingValue ?? ''}`.trim().toLowerCase();

        if (parsedValue === '') {
            setInvalid(false);
            setErrorLabel('');
        }

        setReceiverEmail(parsedValue);
    };

    const unblockedTime = useMemo(() => {
        let copy = '';

        if (blockedUntil) {
            const transferBlockedUntil = TimeAgo.inWords(Date.parse(blockedUntil));
            copy = translate('failed_transfer_alert_message', {
                yat: translate('translation:yat'),
                unblockedTime: transferBlockedUntil,
            });
        }

        return copy;
    }, [blockedUntil, translate]);

    const handleNext = useCallback(() => {
        if (transferBlocked) {
            setInvalid(true);
            setErrorLabel(unblockedTime);
            return;
        }

        if (!validated) {
            setInvalid(true);
            return;
        }

        setInvalid(false);

        apiFactory()
            .call(Transfers.create, {
                prefetch: prefetch2FA(dispatch),
                value: {
                    eid,
                    email: receiverEmail,
                    clear_on_transfer: true,
                    force_transfer: false,
                },
            })
            .then((result) => {
                if (result.ok) {
                    dispatch(
                        setTransferData(eid, {
                            id: result.value.id,
                            status: TransferStatus.Initiated,
                            recipient_email: receiverEmail,
                        }),
                    );
                } else {
                    setErrorLabel(result.error.toString());
                }
            })
            .catch((error) => {
                if (error) {
                    setInvalid(true);
                    setErrorLabel(error.message);
                    dispatch(addNotification({ message: error.message, severity: 'error' }));
                }
            });
    }, [eid, dispatch, receiverEmail, validated, transferBlocked, unblockedTime]);

    useEffect(() => {
        setInvalid(isOwnEmail);
        setErrorLabel(translate('initiation_error_copy'));
    }, [isOwnEmail, translate]);

    useEffect(() => {
        if (blockedUntil) {
            const now = Date.parse(new Date().toISOString());
            const blocked = Date.parse(blockedUntil);
            setTransferBlocked(blocked > now);
        } else {
            setTransferBlocked(false);
        }
    }, [blockedUntil]);

    const hasError = Boolean(invalid && errorLabel.length);

    const errorMarkup = hasError ? <div className="yat-input__error-label">{errorLabel}</div> : null;

    return (
        <div className="yat-transfer-sidebar__step">
            <div className="yat-transfer-sidebar__flex-item">
                <h1 className="yat-transfer-sidebar__title">
                    {translate('initiation_title', {
                        yat: translate('translation:yat'),
                    })}
                </h1>
                <p
                    className="yat-transfer-sidebar__copy v2-text-fluid"
                    dangerouslySetInnerHTML={{
                        __html: translate('initiation_body_copy', {
                            yat: translate('translation:yat'),
                        }),
                    }}
                />

                <h2
                    className="yat-transfer-sidebar__subtitle"
                    dangerouslySetInnerHTML={{
                        __html: translate('initiation_subtitle', {
                            yat: translate('translation:yat'),
                        }),
                    }}
                />
                <p className="yat-transfer-sidebar__copy yat-transfer-sidebar__copy--warning">
                    {translate('initiation_warning_copy', {
                        yat: translate('translation:yat'),
                    })}
                </p>
            </div>

            <div className="yat-transfer-sidebar__flex-item">
                <div className="yat-transfer-sidebar__input">
                    <FieldEmail
                        label={translate('common:email')}
                        value={receiverEmail}
                        error={hasError}
                        onChange={handleEmailChange}
                    />

                    {errorMarkup}
                </div>

                {/* TODO: Needs a loading state upon `handleNext()` */}
                <DepButton
                    display={DepButtonDisplay.minWidth}
                    variant="primary"
                    size="large"
                    disabled={!validated}
                    onClick={handleNext}
                >
                    {translate('initiation_cta_copy')}
                </DepButton>
            </div>
        </div>
    );
}
